import { type Dispatch } from 'react';

import type { DraftEntry, WeightUnit } from './state/loggerDraft';
import type { LoggerAction } from './state/loggerDraftTypes';

// SetCountRow ＋/− handlers, extracted from useTimelineLogger to keep that hook
// under the 500-line cap (LG-14 split). In a superset/circuit ＋/− fans out to
// EVERY member so the group stays symmetric (Antenore 2026-06-01), a standalone
// ＋ appends one set seeded from the previous one (seedNextSet's carry-forward,
// same as the group path), and each handler clears the manual selection so the
// new binding follows.
export function useSetCountHandlers(args: {
  activeEntryIdx: number;
  activeEntry: DraftEntry | null;
  defaultUnit: WeightUnit;
  /** Trailing set index of the active entry (= the group's last round). */
  lastSetIdx: number;
  canRemove: boolean;
  dispatch: Dispatch<LoggerAction>;
  setSelectedSetIdx: (idx: number | null) => void;
  followHoldInsert?: (entryIdx: number, setIdx: number) => void;
}) {
  const {
    activeEntryIdx,
    activeEntry,
    defaultUnit,
    lastSetIdx,
    canRemove,
    dispatch,
    setSelectedSetIdx,
    followHoldInsert,
  } = args;

  const onAddSet = () => {
    if (activeEntryIdx < 0) return;
    if (activeEntry?.group) {
      dispatch({
        type: 'add_round_to_group',
        groupId: activeEntry.group.id,
        defaultUnit,
      });
    } else {
      dispatch({ type: 'add_set', entryIdx: activeEntryIdx, defaultUnit });
    }
    setSelectedSetIdx(null);
  };

  const onRemoveSet = () => {
    if (activeEntryIdx < 0 || !canRemove) return;
    if (activeEntry?.group) {
      dispatch({
        type: 'remove_round_from_group',
        groupId: activeEntry.group.id,
        roundIdx: lastSetIdx,
      });
    } else {
      dispatch({ type: 'remove_set', entryIdx: activeEntryIdx, setIdx: lastSetIdx });
    }
    setSelectedSetIdx(null);
  };

  // LG-77 — insert a set BEFORE `setIdx`, from the row's own pointer.
  //
  // ⛔ It does NOT fan out to a superset's other members, unlike ＋/−. Those
  // two keep the group symmetric because they change the number of ROUNDS,
  // and a round is a group-wide thing. This one puts an extra set in front of
  // one exercise — a warm-up on the bench, a preparation set before a heavy
  // single — which is exactly a thing that belongs to that exercise alone.
  // Fanning it out would invent sets on the other members that the athlete
  // never asked for, and there is no round to keep symmetric in the first
  // place. If living with it says otherwise, that is a decision, not a bug.
  const onInsertSetBefore = (setIdx: number) => {
    if (activeEntryIdx < 0) return;
    dispatch({
      type: 'insert_set_at',
      entryIdx: activeEntryIdx,
      setIdx,
      defaultUnit,
    });
    followHoldInsert?.(activeEntryIdx, setIdx);
    // Bind the set that was just inserted: it is at `setIdx` now, and it is
    // the one the athlete is about to fill in. The ＋/− handlers clear the
    // selection instead because they act on the tail, which the default
    // binding already reaches.
    setSelectedSetIdx(setIdx);
  };

  return { onAddSet, onRemoveSet, onInsertSetBefore };
}
